import { TextInput } from '@ztechapps/webui.input'
import { SpreadsheetCellShell, SpreadsheetCellViewValue } from '@ztechapps/webui.data-tools'
import { MetadataField, useMetadataContext } from './Context'
import { CommonMetadataSelect } from './CommonMetadataSelect'
import { UnitSelect } from './UnitSelect'
import { GridColumn } from './GridColumn'

/**
 * Auto-trailing empty row at the bottom of the Specifications grid. Rendered via `Table.foot`
 * (`<tfoot>`) so it sits OUTSIDE the `<tbody>` that `useDragSort` tracks — no
 * `data-drag-sort-index`, cannot be dragged, cannot be a drop target. The trailing row is never
 * part of the saved payload; `list` is the source of truth and this row is render-only.
 *
 * Typing into any of the three cells calls `onTrailingRowChange(field, value, ...)`, which
 * appends a new `MetadataListItem` to `list`. Because `rowCount = list.length + 1` in the
 * navigation hook, a fresh empty trailing row re-renders below; `activeCell` is pinned to the
 * just-promoted row so the cursor stays in place. `key={list.length}` on the `<tfoot>` forces a
 * remount on each promotion so the new trailing row genuinely starts blank.
 *
 * The drag-handle and trash-icon cells are placeholder `<td />` slots for visual alignment only.
 */
export const TrailingEmptyRow = () => {
  const { list, metadataSchema, hasRole, getCellProps, exitEditMode, selectCell, onTrailingRowChange } =
    useMetadataContext()

  const rowIndex = list.length

  const nameCell = getCellProps(rowIndex, GridColumn.Name)
  const valueCell = getCellProps(rowIndex, GridColumn.Value)
  const unitCell = getCellProps(rowIndex, GridColumn.Unit)

  const focusCell = (col: number) => () => selectCell(rowIndex, col)

  return (
    <tfoot key={list.length}>
      <tr>
        {/* Drag-handle slot — placeholder. Trailing row is never draggable. */}
        <td tabIndex={-1} />

        {/* Name (Keys) */}
        <SpreadsheetCellShell
          isActive={nameCell.isActive}
          isEditing={nameCell.isEditing}
          tabIndex={nameCell.tabIndex}
          className="p-0"
        >
          {nameCell.isEditing ? (
            <CommonMetadataSelect
              value={undefined}
              onChange={(e) => {
                const value = e?.target?.value ?? e?.value
                onTrailingRowChange(MetadataField.Key, value, { currentMetadataSchema: metadataSchema?.[value] })
              }}
              spreadsheetMode={{
                onCommit: exitEditMode,
                defaultMenuIsOpen: true,
                defaultInputValue: nameCell.initialEditChar ?? undefined,
              }}
            />
          ) : (
            <SpreadsheetCellViewValue onClick={focusCell(GridColumn.Name)} text="" />
          )}
        </SpreadsheetCellShell>

        {/* Value */}
        <SpreadsheetCellShell
          isActive={valueCell.isActive}
          isEditing={valueCell.isEditing}
          tabIndex={valueCell.tabIndex}
          className="p-0"
        >
          {valueCell.isEditing ? (
            <TextInput
              disabled={!hasRole}
              autoFocus
              onFocus={(e) => {
                // Same rationale as `ListRow.tsx` → `placeCursorAtEnd`: default `TextInput` onFocus
                // selects the entire value; we want cursor at end so the pre-injected initial char
                // is followed by further typing instead of being replaced.
                const target = e.currentTarget as HTMLInputElement
                const len = target.value.length
                try {
                  target.setSelectionRange(len, len)
                } catch {
                  // Number inputs in Chromium don't support setSelectionRange.
                }
              }}
              value={undefined}
              onChange={(e: any) => {
                const value = e?.target?.value ?? e?.value ?? e
                onTrailingRowChange(MetadataField.Value, value)
              }}
            />
          ) : (
            <SpreadsheetCellViewValue onClick={focusCell(GridColumn.Value)} text="" />
          )}
        </SpreadsheetCellShell>

        {/* Unit */}
        <SpreadsheetCellShell
          isActive={unitCell.isActive}
          isEditing={unitCell.isEditing}
          tabIndex={unitCell.tabIndex}
          className="p-0"
        >
          {unitCell.isEditing ? (
            <UnitSelect
              value={undefined}
              onChange={(e) => {
                onTrailingRowChange(MetadataField.Unit, e?.value)
              }}
              inputName={undefined}
              spreadsheetMode={{
                onCommit: exitEditMode,
                defaultMenuIsOpen: true,
                defaultInputValue: unitCell.initialEditChar ?? undefined,
              }}
            />
          ) : (
            <SpreadsheetCellViewValue onClick={focusCell(GridColumn.Unit)} text="" />
          )}
        </SpreadsheetCellShell>

        {/* Trash-icon slot — placeholder. Trailing row has nothing to delete. `width: 1` matches
            the data row's trash column so the trailing row's columns align with the rows above. */}
        <td tabIndex={-1} style={{ width: 1 }} />
      </tr>
    </tfoot>
  )
}
